import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Building2, Calendar as CalendarIcon, FileText } from "lucide-react";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { STATUS_BADGE, cn, formatDate } from "@/lib/utils";
import api from "@/lib/api";
import { translateFr as tfr } from "@/i18n";

export default function SearchPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open) { setQuery(""); setResults(null); return; }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (query.trim().length < 2) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const r = await api.get("/search", { params: { q: query.trim() }, signal: controller.signal });
        setResults(r.data);
      } catch (e) { /* aborted or failed */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [query]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  const hasResults = results && (
    results.players?.length || results.clubs?.length || results.events?.length || results.documents?.length
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <CommandInput
        placeholder={tfr("Rechercher un joueur, club, événement, document…")}
        value={query}
        onValueChange={setQuery}
        data-testid="search-palette-input"
      />
      <CommandList data-testid="search-palette-results">
        {query.trim().length < 2 && (
          <div className="py-8 text-center text-xs text-muted-foreground">
            {tfr("Tape au moins 2 caractères pour rechercher.")}
          </div>
        )}
        {query.trim().length >= 2 && !loading && !hasResults && (
          <CommandEmpty>Aucun résultat pour « {query} ».</CommandEmpty>
        )}
        {results?.players?.length > 0 && (
          <CommandGroup heading="Joueurs">
            {results.players.map((p) => (
              <CommandItem key={p.id} value={`player-${p.id}`} onSelect={() => go(`/players/${p.id}`)} data-testid={`search-player-${p.id}`}>
                <User className="h-4 w-4 mr-2 text-muted-foreground" />
                <span className="flex-1 truncate">{p.name}</span>
                <span className="text-xs text-muted-foreground mr-2 truncate">{p.club}</span>
                <Badge variant="outline" className={cn("text-[10px] border", STATUS_BADGE[p.status])}>{p.status}</Badge>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results?.clubs?.length > 0 && (
          <CommandGroup heading="Clubs">
            {results.clubs.map((c) => (
              <CommandItem key={c.id} value={`club-${c.id}`} onSelect={() => go("/clubs")} data-testid={`search-club-${c.id}`}>
                <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                <span className="flex-1 truncate">{c.name}</span>
                <span className="text-xs text-muted-foreground truncate">{c.league}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results?.events?.length > 0 && (
          <CommandGroup heading={tfr("Événements")}>
            {results.events.map((e) => (
              <CommandItem key={e.id} value={`event-${e.id}`} onSelect={() => go("/calendar")} data-testid={`search-event-${e.id}`}>
                <CalendarIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                <span className="flex-1 truncate">{e.title}</span>
                <span className="text-xs text-muted-foreground">{formatDate(e.starts_at)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results?.documents?.length > 0 && (
          <CommandGroup heading="Documents">
            {results.documents.map((d) => (
              <CommandItem key={d.id} value={`doc-${d.id}`} onSelect={() => go("/documents")} data-testid={`search-doc-${d.id}`}>
                <FileText className="h-4 w-4 mr-2 text-muted-foreground" />
                <span className="flex-1 truncate">{d.label}</span>
                {d.expires_at && <span className="text-xs text-muted-foreground">exp. {formatDate(d.expires_at)}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
